import { useEffect, useRef } from 'react'
import { ChevronRight, GripVertical, Plus, Trash2 } from 'lucide-react'
import type { RequirementNode, RequirementPriorityLevel } from '@ispot/shared'

const PRIORITY_DOT: Record<NonNullable<RequirementPriorityLevel> | 'none', string> = {
  critical: 'var(--color-danger)',
  high: 'var(--color-accent)',
  medium: 'var(--color-solution)',
  low: 'var(--color-warm-300)',
  none: 'var(--color-warm-300)',
}

interface OutlineRowProps {
  node: RequirementNode
  depth: 0 | 1
  isActive: boolean
  draggingId: string | null
  dropEdge: 'top' | 'bottom' | null
  onOpenDetail: (id: string) => void
  onCommit: (id: string, patch: { name?: string; description?: string }) => void
  onEnterAfter: (id: string) => void
  onIndent: (id: string) => void
  onOutdent: (id: string) => void
  onDeleteIfEmpty: (id: string) => void
  onArrow: (id: string, dir: 1 | -1) => void
  onAddBelow: (id: string) => void
  onDelete: (id: string) => void
  onDragStart: (id: string) => void
  onDragOver: (id: string, edge: 'top' | 'bottom') => void
  onDragEnd: () => void
  onDrop: (id: string) => void
}

function placeCaretEnd(el: HTMLElement) {
  const range = document.createRange()
  range.selectNodeContents(el)
  range.collapse(false)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}

export function OutlineRow(props: OutlineRowProps) {
  const {
    node, depth, isActive, draggingId, dropEdge,
    onOpenDetail, onCommit, onEnterAfter, onIndent, onOutdent,
    onDeleteIfEmpty, onArrow, onAddBelow, onDelete,
    onDragStart, onDragOver, onDragEnd, onDrop,
  } = props

  const titleRef = useRef<HTMLSpanElement>(null)
  const descRef = useRef<HTMLSpanElement>(null)

  // Initial mount + id-change: hard sync DOM to value.
  useEffect(() => {
    if (titleRef.current) titleRef.current.textContent = node.name ?? ''
    if (descRef.current) descRef.current.textContent = node.description ?? ''
  }, [node.id])

  // Reflect server-side updates while not focused.
  useEffect(() => {
    const el = titleRef.current
    if (!el || document.activeElement === el) return
    if (el.textContent !== (node.name ?? '')) el.textContent = node.name ?? ''
  }, [node.name])
  useEffect(() => {
    const el = descRef.current
    if (!el || document.activeElement === el) return
    if (el.textContent !== (node.description ?? '')) el.textContent = node.description ?? ''
  }, [node.description])

  const isEpic = node.type === 'epic' || node.type === 'nfr-category'
  const ac = Array.isArray(node.metadata?.acceptanceCriteria) ? node.metadata!.acceptanceCriteria! : []
  const acDone = ac.filter((c: { done?: boolean; met?: boolean }) => c.done ?? c.met).length

  const titleEmpty = !node.name || node.name.trim() === ''
  const descEmpty = !node.description || node.description.trim() === ''

  function handleInput(e: React.FormEvent<HTMLSpanElement>) {
    e.currentTarget.dataset.empty = (e.currentTarget.textContent ?? '').trim() === '' ? 'true' : 'false'
  }

  function handleKey(e: React.KeyboardEvent<HTMLSpanElement>, field: 'name' | 'description') {
    const el = e.currentTarget
    const text = el.textContent ?? ''
    const flush = () => {
      const cur = node[field === 'name' ? 'name' : 'description'] ?? ''
      if (text !== cur) onCommit(node.id, { [field]: text })
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); flush(); onEnterAfter(node.id); return
    }
    if (e.key === 'Tab') {
      e.preventDefault(); flush()
      if (e.shiftKey) onOutdent(node.id); else onIndent(node.id); return
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault(); flush(); el.blur(); onOpenDetail(node.id); return
    }
    if (e.key === 'Backspace' && text.trim() === '') {
      if (field === 'name' && (!node.description || node.description.trim() === '')) {
        e.preventDefault(); onDeleteIfEmpty(node.id); return
      }
      if (field === 'description') {
        e.preventDefault()
        if (titleRef.current) { titleRef.current.focus(); placeCaretEnd(titleRef.current) }
        return
      }
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); onArrow(node.id, 1); return }
    if (e.key === 'ArrowUp')   { e.preventDefault(); onArrow(node.id, -1); return }
    if (e.key === 'Escape')    { e.preventDefault(); el.blur() }
  }

  function handleBlur(field: 'name' | 'description') {
    const el = field === 'name' ? titleRef.current : descRef.current
    if (!el) return
    const text = el.textContent ?? ''
    const cur = node[field === 'name' ? 'name' : 'description'] ?? ''
    if (text !== cur) onCommit(node.id, { [field]: text })
  }

  function handleRowClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement
    if (target.closest('[data-outline-edit]')) return
    if (target.closest('[data-action]')) return
    if (titleRef.current) { titleRef.current.focus(); placeCaretEnd(titleRef.current) }
  }

  const rowClass = [
    'outline-row',
    `depth-${depth}`,
    isEpic ? 'is-epic' : '',
    isActive ? 'is-active' : '',
    draggingId === node.id ? 'is-dragging' : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      data-outline-id={node.id}
      draggable
      className={rowClass}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move'
        try { e.dataTransfer.setData('text/plain', node.id) } catch { /* noop */ }
        onDragStart(node.id)
      }}
      onDragOver={(e) => {
        e.preventDefault()
        const rect = e.currentTarget.getBoundingClientRect()
        onDragOver(node.id, e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom')
      }}
      onDragEnd={onDragEnd}
      onDrop={(e) => { e.preventDefault(); onDrop(node.id) }}
      onClick={handleRowClick}
    >
      {dropEdge && <span aria-hidden className={`outline-drop-line ${dropEdge}`} />}

      <div className="outline-gutter">
        <span className="outline-gutter-btn" data-action="drag" title="Drag to reorder">
          <GripVertical size={12} />
        </span>
        <button
          type="button"
          className="outline-gutter-btn"
          data-action="add-below"
          title="Add below"
          onClick={(e) => { e.stopPropagation(); onAddBelow(node.id) }}
        >
          <Plus size={12} />
        </button>
      </div>

      <span
        className={`outline-bullet ${isEpic ? 'is-epic' : ''}`}
        data-action="open-pane"
        role="button"
        aria-label="Open detail"
        onClick={() => onOpenDetail(node.id)}
        style={isEpic ? undefined : { background: PRIORITY_DOT[node.priority ?? 'none'] }}
      >{isEpic ? <ChevronRight size={13} strokeWidth={2.2} /> : ''}</span>

      <div className="outline-title-wrap">
        <span
          ref={titleRef}
          data-outline-edit
          className="outline-title"
          contentEditable
          spellCheck={false}
          suppressContentEditableWarning
          data-empty={titleEmpty ? 'true' : 'false'}
          data-placeholder={isEpic ? 'Untitled epic' : 'Untitled requirement'}
          onKeyDown={(e) => handleKey(e, 'name')}
          onInput={handleInput}
          onBlur={() => handleBlur('name')}
        />
        <span
          ref={descRef}
          data-outline-edit
          className="outline-desc"
          contentEditable
          spellCheck={false}
          suppressContentEditableWarning
          data-empty={descEmpty ? 'true' : 'false'}
          data-placeholder="Add description…"
          onKeyDown={(e) => handleKey(e, 'description')}
          onInput={handleInput}
          onBlur={() => handleBlur('description')}
        />
      </div>

      <div className="outline-trail">
        {!isEpic && ac.length > 0 && (
          <span
            data-action="open-pane"
            className="outline-ac"
            onClick={() => onOpenDetail(node.id)}
            title="Acceptance criteria"
          >
            {acDone}/{ac.length}
          </span>
        )}
        <button
          type="button"
          data-action="delete"
          className="outline-delete-btn"
          title="Delete"
          onClick={(e) => { e.stopPropagation(); onDelete(node.id) }}
        >
          <Trash2 size={12} />
        </button>
        <button
          type="button"
          data-action="open-pane"
          className="outline-open-btn"
          onClick={() => onOpenDetail(node.id)}
          title="Open detail (⌘↩)"
        >
          <ChevronRight size={12} />
        </button>
      </div>
    </div>
  )
}
